<script lang="ts">
    const BASE_URL = "https://overwatchbrain.com";
    const BRAND = "OverwatchBrain";

    /**
     * Default OG image path (root-relative).
     * Replace with a custom branded graphic when available by dropping it at
     * static/images/og/default.png and updating this path — or override
     * per-page via the `image` prop.
     */
    const DEFAULT_IMAGE = "/images/headers/tracer.jpg";

    interface Breadcrumb {
        name: string;
        path: string;
    }

    interface Faq {
        question: string;
        answer: string;
    }

    interface Props {
        /** Descriptive page title WITHOUT brand suffix — the component appends " | OverwatchBrain" automatically. */
        title: string;
        description: string;
        path: string;
        /** Root-relative path to the OG image (e.g. "/images/og/tier-list.png"). */
        image?: string;
        /** Prevents search engines from indexing this specific view (e.g. filtered pages with query params). */
        noindex?: boolean;
        /** Ordered breadcrumb trail for BreadcrumbList structured data. */
        breadcrumbs?: Breadcrumb[];
        /** Q&A pairs emitted as FAQPage structured data (must match visible on-page FAQs). */
        faqs?: Faq[];
    }

    let { title, description, path, image = DEFAULT_IMAGE, noindex = false, breadcrumbs, faqs }: Props = $props();

    const fullTitle = $derived(`${title} | ${BRAND}`);
    const canonicalUrl = $derived(`${BASE_URL}${path}`);
    const imageUrl = $derived(`${BASE_URL}${image}`);

    const jsonLd = $derived.by(() => {
        if (path === "/") {
            return JSON.stringify({
                "@context": "https://schema.org",
                "@type": "WebSite",
                name: BRAND,
                alternateName: ["OWBrain", "overwatchbrain.com"],
                url: BASE_URL,
                description,
            });
        }
        return JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            name: fullTitle,
            description,
            url: canonicalUrl,
            isPartOf: {
                "@type": "WebSite",
                name: BRAND,
                url: BASE_URL,
            },
        });
    });

    const breadcrumbJsonLd = $derived.by(() => {
        if (!breadcrumbs?.length) return null;
        const items = [
            { "@type": "ListItem", position: 1, name: "Home", item: BASE_URL },
            ...breadcrumbs.map((b, i) => ({
                "@type": "ListItem",
                position: i + 2,
                name: b.name,
                item: `${BASE_URL}${b.path}`,
            })),
        ];
        return JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: items,
        });
    });

    const faqJsonLd = $derived.by(() => {
        if (!faqs?.length) return null;
        return JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: faqs.map((f) => ({
                "@type": "Question",
                name: f.question,
                acceptedAnswer: { "@type": "Answer", text: f.answer },
            })),
        });
    });

    /** Wraps JSON-LD in a script tag. Built in JS context to avoid confusing the ESLint Svelte parser. */
    function ldTag(json: string): string {
        return '<script type="application/ld+json">' + json + "</" + "script>";
    }
</script>

<svelte:head>
    <title>{fullTitle}</title>
    <meta name="description" content={description} />
    {#if noindex}
        <meta name="robots" content="noindex, follow" />
    {/if}

    <!-- Canonical -->
    <link rel="canonical" href={canonicalUrl} />

    <!-- Open Graph -->
    <meta property="og:title" content={fullTitle} />
    <meta property="og:description" content={description} />
    <meta property="og:url" content={canonicalUrl} />
    <meta property="og:type" content="website" />
    <meta property="og:site_name" content={BRAND} />
    <meta property="og:image" content={imageUrl} />
    <meta property="og:image:width" content="1200" />
    <meta property="og:image:height" content="630" />

    <!-- Twitter Card -->
    <meta name="twitter:card" content="summary_large_image" />
    <meta name="twitter:title" content={fullTitle} />
    <meta name="twitter:description" content={description} />
    <meta name="twitter:image" content={imageUrl} />

    <!-- Structured Data (developer-authored JSON-LD, not user input) -->
    <!-- eslint-disable-next-line svelte/no-at-html-tags -->
    {@html ldTag(jsonLd)}
    {#if breadcrumbJsonLd}
        <!-- eslint-disable-next-line svelte/no-at-html-tags -->
        {@html ldTag(breadcrumbJsonLd)}
    {/if}
    {#if faqJsonLd}
        <!-- eslint-disable-next-line svelte/no-at-html-tags -->
        {@html ldTag(faqJsonLd)}
    {/if}
</svelte:head>
